import { Session, User as SupabaseUser, AuthError } from '@supabase/supabase-js';
import { User, CreatorProfile } from '@/types';
import { supabase } from '../supabase/client';
import { authMonitor } from './monitoring';
import { traceAuthFlow } from './debug';
import { authRateLimiter } from './rate-limiter';
import { tokenManager } from './token-manager';
import { logger } from '../utils/logger';

// Simple permission types for this service
type Permission = string;
type Role = 'user' | 'creator' | 'admin';

/**
 * Unified Auth Service
 * 
 * Single source of truth for authentication and user profile management.
 * Eliminates circular dependencies by combining auth and profile concerns.
 * 
 * Features:
 * - Unified session and profile state
 * - Automatic profile fetching on auth
 * - Built-in caching with TTL
 * - Permission checking
 * - Event-driven architecture
 */
export class UnifiedAuthService {
  // Auth state
  private session: Session | null = null;
  private loading = true;
  private initialized = false;
  
  // Profile state
  private userProfile: User | null = null;
  private creatorProfile: CreatorProfile | null = null;
  private profileCacheTimestamp = 0;
  private readonly profileCacheTTL = 5 * 60 * 1000; // 5 minutes
  
  // Event listeners
  private authListeners = new Set<(session: Session | null) => void>();
  private profileListeners = new Set<(user: User | null) => void>();
  private errorListeners = new Set<(error: Error) => void>();
  
  constructor() {
    this.initialize();
  }
  
  private async initialize() {
    try {
      // Check rate limit
      if (!authRateLimiter.canMakeRequest('sessionRestore')) {
        logger.warn('Rate limit hit during session restore');
        this.setInitialized();
        return;
      }
      
      authMonitor.startTimer('sessionRestore');
      traceAuthFlow('UnifiedAuthService.initialize');
      
      // Restore session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        authMonitor.endTimer('sessionRestore', false, error);
        logger.error('Failed to restore session', { error });
        
        if (this.isInvalidTokenError(error)) {
          await this.clearInvalidSession();
        } else {
          this.notifyError(error);
        }
        
        this.setInitialized();
        return;
      }
      
      if (session) {
        this.session = session;
        // Fetch profile in background
        this.fetchProfileInBackground(session.user.id);
      }
      
      authMonitor.endTimer('sessionRestore', true);
      this.setInitialized();
      
      // Set up auth state listener
      supabase.auth.onAuthStateChange(this.handleAuthStateChange.bind(this));
      
    } catch (error) {
      authMonitor.endTimer('sessionRestore', false, error as Error);
      logger.error('Failed to initialize auth', { error });
      this.notifyError(error as Error);
      this.setInitialized();
    }
  }
  
  private setInitialized() {
    this.loading = false;
    this.initialized = true;
    this.notifyAuthListeners(this.session);
    this.notifyProfileListeners(this.userProfile);
  }
  
  private async handleAuthStateChange(event: string, session: Session | null) {
    traceAuthFlow(`AuthStateChange: ${event}`, { userId: session?.user?.id });
    
    switch (event) {
      case 'SIGNED_IN':
        this.session = session;
        logger.info('User signed in', { email: session?.user?.email });
        if (session) {
          await this.fetchProfileInBackground(session.user.id);
        }
        break;
        
      case 'SIGNED_OUT':
        this.clearState();
        logger.info('User signed out');
        break;
        
      case 'TOKEN_REFRESHED':
        if (!session) {
          logger.warn('Token refresh failed');
          await this.signOut();
        } else {
          this.session = session;
        }
        break;
        
      case 'USER_UPDATED':
        this.session = session;
        logger.info('User updated', { email: session?.user?.email });
        if (session) {
          await this.fetchProfileInBackground(session.user.id, true);
        }
        break;
    }
    
    this.notifyAuthListeners(session);
  }
  
  private async fetchProfileInBackground(userId: string, forceRefresh = false) {
    try {
      // Check cache validity
      if (!forceRefresh && this.isProfileCacheValid()) {
        return;
      }
      
      authMonitor.startTimer('profileFetch');
      
      // Use faster single query with timeout for mobile optimization
      const fetchPromise = supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      // Add timeout for mobile responsiveness
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), 3000)
      );
      
      const { data: user, error: userError } = await Promise.race([
        fetchPromise,
        timeoutPromise
      ]) as any;
        
      if (userError) {
        logger.error('Failed to fetch user profile', { error: userError });
        authMonitor.endTimer('profileFetch', false, userError);
        return;
      }
      
      // Map the user data and provide default status if missing
      this.userProfile = {
        ...user,
        status: user.status || 'active' // default to 'active' if missing
      };
      this.profileCacheTimestamp = Date.now();
      
      // Notify immediately for faster UI updates
      this.notifyProfileListeners(this.userProfile);
      
      // Fetch creator profile in parallel if user is a creator (don't block UI)
      if (user?.role === 'creator' || user?.role === 'admin') {
        // Use explicit field selection to avoid 406 errors on mobile
        const fetchCreatorProfile = async () => {
          try {
            const { data: creator, error } = await supabase
              .from('creator_profiles')
              .select('*')
              .eq('user_id', userId)
              .maybeSingle();
            
            if (error) {
              logger.warn('Creator profile fetch failed', { error });
              this.creatorProfile = null;
            } else {
              this.creatorProfile = creator;
            }
            // Notify again with complete data
            this.notifyProfileListeners(this.userProfile);
          } catch (error) {
            logger.warn('Creator profile fetch error', { error });
            this.creatorProfile = null;
          }
        };
        
        fetchCreatorProfile();
      } else {
        this.creatorProfile = null;
      }
      
      authMonitor.endTimer('profileFetch', true);
      
    } catch (error) {
      logger.error('Error fetching profile', { error });
      authMonitor.endTimer('profileFetch', false, error as Error);
    }
  }
  
  private isProfileCacheValid(): boolean {
    return (
      this.userProfile !== null &&
      Date.now() - this.profileCacheTimestamp < this.profileCacheTTL
    );
  }
  
  private isInvalidTokenError(error: any): boolean {
    return (
      error?.message?.includes('refresh_token_not_found') ||
      error?.message?.includes('invalid refresh token') ||
      error?.message?.includes('Token has expired') ||
      error?.status === 400 ||
      error?.status === 401
    );
  }
  
  private async clearInvalidSession() {
    logger.warn('Clearing invalid session');
    await supabase.auth.signOut();
    this.clearState();
  }
  
  private clearState() {
    this.session = null;
    this.userProfile = null;
    this.creatorProfile = null;
    this.profileCacheTimestamp = 0;
    // Notify listeners of the state change
    this.notifyAuthListeners(null);
    this.notifyProfileListeners(null);
    tokenManager.reset();
  }
  
  // Public API - Authentication
  
  async signIn(email: string, password: string) {
    try {
      authMonitor.startTimer('signIn');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        authMonitor.endTimer('signIn', false, error);
        throw error;
      }
      
      authMonitor.endTimer('signIn', true);
      return data;
      
    } catch (error) {
      logger.error('Sign in failed', { error });
      throw error;
    }
  }
  
  async signInWithProvider(provider: 'google') {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      
    } catch (error) {
      logger.error('OAuth sign in failed', { error });
      throw error;
    }
  }
  
  async signUp(email: string, password: string, metadata?: Record<string, any>) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata }
      });
      
      if (error) throw error;
      return data;
      
    } catch (error) {
      logger.error('Sign up failed', { error });
      throw error;
    }
  }
  
  async signOut() {
    try {
      logger.info('Starting sign out process');
      const { error } = await supabase.auth.signOut();
      if (error) {
        logger.error('Supabase signOut returned error', { error });
        throw error;
      }
      this.clearState();
      logger.info('Sign out completed successfully');
    } catch (error) {
      logger.error('Sign out failed', { error });
      // Clear state even on error
      this.clearState();
      throw error;
    }
  }
  
  async refreshSession() {
    try {
      const session = await tokenManager.refreshToken();
      return session;
    } catch (error) {
      logger.error('Session refresh failed', { error });
      throw error;
    }
  }
  
  // Public API - Profile Management
  
  async getProfile(forceRefresh = false): Promise<User | null> {
    if (!this.session) return null;
    
    if (forceRefresh || !this.isProfileCacheValid()) {
      await this.fetchProfileInBackground(this.session.user.id, forceRefresh);
    }
    
    return this.userProfile;
  }
  
  async updateProfile(updates: Partial<User>) {
    if (!this.session) throw new Error('Not authenticated');
    
    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', this.session.user.id)
        .select()
        .single();
        
      if (error) throw error;
      
      this.userProfile = data;
      this.profileCacheTimestamp = Date.now();
      this.notifyProfileListeners(data);
      
      return data;
      
    } catch (error) {
      logger.error('Profile update failed', { error });
      throw error;
    }
  }
  
  // Public API - State Getters
  
  getSession(): Session | null {
    return this.session;
  }
  
  getUser(): SupabaseUser | null {
    return this.session?.user ?? null;
  }
  
  getUserProfile(): User | null {
    return this.userProfile;
  }
  
  getCreatorProfile(): CreatorProfile | null {
    return this.creatorProfile;
  }
  
  isAuthenticated(): boolean {
    return !!this.session;
  }
  
  isLoading(): boolean {
    return this.loading;
  }
  
  isInitialized(): boolean {
    return this.initialized;
  }
  
  // Public API - Permissions
  
  hasPermission(permission: Permission): boolean {
    if (!this.userProfile) return false;
    const role = this.userProfile.role as Role;
    
    // Simple permission matrix
    const permissions: Record<Role, Set<Permission>> = {
      user: new Set([
        'profile.view',
        'profile.edit.own',
        'experiences.view',
        'experiences.book',
        'bookings.view.own'
      ]),
      creator: new Set([
        'profile.view',
        'profile.edit.own',
        'experiences.view',
        'experiences.book',
        'experiences.create',
        'experiences.edit.own',
        'bookings.view.own',
        'bookings.view.for_experiences',
        'creator.dashboard.view'
      ]),
      admin: new Set(['*'])
    };
    
    const rolePermissions = permissions[role];
    return rolePermissions.has('*') || rolePermissions.has(permission);
  }
  
  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }
  
  hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }
  
  // Public API - Event Subscriptions
  
  onAuthStateChange(listener: (session: Session | null) => void): () => void {
    this.authListeners.add(listener);
    
    // Immediately notify if initialized
    if (this.initialized) {
      listener(this.session);
    }
    
    return () => {
      this.authListeners.delete(listener);
    };
  }
  
  onProfileChange(listener: (user: User | null) => void): () => void {
    this.profileListeners.add(listener);
    
    // Immediately notify if initialized
    if (this.initialized) {
      listener(this.userProfile);
    }
    
    return () => {
      this.profileListeners.delete(listener);
    };
  }
  
  onError(listener: (error: Error) => void): () => void {
    this.errorListeners.add(listener);
    
    return () => {
      this.errorListeners.delete(listener);
    };
  }
  
  // Private helpers
  
  private notifyAuthListeners(session: Session | null) {
    this.authListeners.forEach(listener => {
      try {
        listener(session);
      } catch (error) {
        logger.error('Auth listener error', { error });
      }
    });
  }
  
  private notifyProfileListeners(user: User | null) {
    this.profileListeners.forEach(listener => {
      try {
        listener(user);
      } catch (error) {
        logger.error('Profile listener error', { error });
      }
    });
  }
  
  private notifyError(error: Error) {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (error) {
        logger.error('Error listener error', { error });
      }
    });
  }
}

// Singleton instance
export const unifiedAuthService = new UnifiedAuthService();

// Export for debugging
if (typeof window !== 'undefined') {
  (window as any).unifiedAuthService = unifiedAuthService;
}